import axios from 'axios';

const api = axios.create({
  baseURL: '/api'
});

export const emitServerStatus = (down) => {
  window.dispatchEvent(new CustomEvent('server-status', { detail: { down } }));
};

// ── Anti-tamper integrity verification ──────────────────────────────────────
// Server signs protected responses with HMAC; if someone overrides the response
// body via DevTools Network tab, the signature won't match.
let _integrityKey = null;
// /auth/me excluded — it provides the integrity key itself, verifying it
// would use a stale key from the previous session and always fail on re-login
const PROTECTED_PATHS = ['/settings', '/enterprise/check', '/licenses/check', '/checkout/status'];

export const setIntegrityKey = (key) => { _integrityKey = key; };

const _te = new TextEncoder();
async function hmacVerify(body, sig, ts) {
  if (!_integrityKey || !sig || !ts) return true; // no key yet = skip
  try {
    const key = await crypto.subtle.importKey(
      'raw', _te.encode(_integrityKey),
      { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']
    );
    const payload = JSON.stringify(body) + ':' + ts;
    const signed = await crypto.subtle.sign('HMAC', key, _te.encode(payload));
    const hex = Array.from(new Uint8Array(signed)).map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 16);
    return hex === sig;
  } catch { return true; }
}

// Require multiple consecutive failures before triggering tamper detection
// This prevents false positives from JSON serialization mismatches,
// race conditions during login, or request timing issues
let _integrityStrikes = 0;
const INTEGRITY_STRIKE_THRESHOLD = 3;

function triggerTamperDetection(/* path */) {
  // Disabled: HMAC verification produces false positives due to JSON
  // re-serialization mismatches (axios parses then re-stringifies,
  // changing key order / number formatting vs. the raw server body).
  // _integrityStrikes++;
  // if (_integrityStrikes >= INTEGRITY_STRIKE_THRESHOLD) {
  //   window.dispatchEvent(new CustomEvent('integrity-violation', { detail: { path } }));
  // }
}

function resetIntegrityStrikes() {
  _integrityStrikes = 0;
}

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  async (response) => {
    // Check integrity on protected paths
    const url = response.config?.url || '';
    if (_integrityKey && PROTECTED_PATHS.some(p => url.includes(p))) {
      const sig = response.headers['x-integrity'];
      const ts = response.headers['x-integrity-ts'];
      if (sig && ts) {
        const valid = await hmacVerify(response.data, sig, ts);
        if (!valid) {
          triggerTamperDetection(url);
        } else {
          resetIntegrityStrikes();
        }
      }
    }
    return response;
  },
  error => {
    const status = error?.response?.status;
    const data = error?.response?.data;
    if (status === 502) {
      emitServerStatus(true);
    }
    if (status === 503 && data?.error === 'maintenance') {
      window.dispatchEvent(new CustomEvent('maintenance-mode', { detail: { message: data.message } }));
    }
    if (status === 403 && data?.error === 'banned') {
      localStorage.removeItem('token');
      const banInfo = data?.ban || {
        reason: data?.reason || 'No reason provided',
        duration: data?.duration || 'Lifetime',
        expires_at: data?.expiresAt,
        banned_at: data?.banned_at,
        banned_by: data?.banned_by
      };
      const params = new URLSearchParams({
        ban: encodeURIComponent(JSON.stringify(banInfo))
      });
      window.location.href = `/banned?${params.toString()}`;
    }
    if (status === 403 && data?.code === 'NO_LICENSE') {
      window.dispatchEvent(new CustomEvent('license-required', { detail: data }));
    }
    if (status === 403 && data?.code === 'NO_DASHBOARD_ACCESS') {
      // Only redirect to /no-license if the user is trying to access a dashboard route
      // Ticket and other non-dashboard endpoints should handle this gracefully
      const isDashboardPath = error.config?.url?.includes('/dashboard') ||
                              error.config?.url?.includes('/enterprise') ||
                              error.config?.url?.includes('/results');
      // Note: /settings is now included in the allowlist since it supports unlicensed users
      if (isDashboardPath) {
        localStorage.removeItem('token');
        window.location.href = '/no-license';
      }
      return Promise.reject(error);
    }

    // Ticket-specific access denial - don't redirect, let component handle it
    if (status === 403 && data?.code === 'NO_TICKET_ACCESS') {
      // Don't redirect; let the component display an error message
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default api;
export { api };
